/**
 * Reine Rechenlogik der Lagerplätze-Seite - bewusst ohne React, damit das
 * Regallayout und die Zuordnungsregel unabhängig von der Darstellung
 * nachvollziehbar bleiben.
 *
 * Statt die Hallenfläche als reine Kachelfläche zu behandeln (in der jeder
 * Quadratmeter Stellfläche wäre), wird hier ein echtes Regallayout gerechnet:
 * Regalzeilen längs der Halle, dazwischen Arbeitsgänge für den Stapler, an
 * beiden Hallenenden Quergänge. Die Kapazität ist damit ein Ergebnis der
 * Rechnung, keine Schätzung mehr (siehe Auftrag).
 *
 * Modul = Doppelregal (zwei Reihen Rücken an Rücken) + ein Arbeitsgang.
 * Modulbreite = 2 × Regaltiefe + Arbeitsgang, z. B. 2 × 1,2 m + 3,2 m = 5,6 m.
 */

/**
 * Randabstand zu den Hallenwänden (Stützen, Sprinkleranlage, Fluchtwege) -
 * eine feste Annahme, kein eigenes Bedienelement, weil der Auftrag dafür
 * keines vorsieht. Wird auf beiden Seiten der Hallenbreite abgezogen, bevor
 * die Anzahl der Regalmodule berechnet wird.
 */
export const EDGE_MARGIN_M = 0.5;

/**
 * Palettenbedarf eines Sortiments für die Hallenverteilung: immer auf volle
 * Paletten aufgerundet (pallets_target_full), genau wie die Stellplatz-
 * Kacheln oben auf der Seite. Mit den ungerundeten pallets_target wies der
 * Hallenvergleich den Bedarf zu niedrig aus (Auftrag #954, Punkt 3).
 */
export function palletsNeed(row) {
    if (row?.pallets_target_full !== null && row?.pallets_target_full !== undefined) {
        return Number(row.pallets_target_full) || 0;
    }
    const raw = Number(row?.pallets_target) || 0;
    return raw > 0 ? Math.ceil(raw - 1e-9) : 0;
}

/**
 * Kapazität einer Halle: aus der Kapazitätsdatei, wenn die Halle dort
 * gezählt ist (Feld fileCapacity, Lahr K28/K29/K31, Plan #64), sonst aus dem
 * Regalmodell computeRackLayout() als Rückfall.
 */
export function warehouseCapacity(warehouse) {
    const fromFile = warehouse?.fileCapacity;
    if (fromFile !== null && fromFile !== undefined && Number.isFinite(Number(fromFile))) {
        return Number(fromFile);
    }
    return computeRackLayout(warehouse).totalCapacity;
}

/**
 * Rechnet aus den Hallenmaßen und den Regalannahmen eines Lagers die
 * tatsächliche Kapazität sowie die Aufteilung in Regalfläche und
 * Verkehrsfläche (Gänge, Quergänge, Randabstand) aus - Punkt 2 im Auftrag.
 *
 * Rechenweg:
 * - Regalmodule   = floor((Hallenbreite − 2 × Randabstand) ÷ Modulbreite)
 * - Stellplätze/Reihe = floor((Hallenlänge − 2 × Quergang) ÷ Stellplatzbreite)
 * - Plätze je Ebene   = Regalmodule × 2 Reihen × Stellplätze/Reihe
 * - Gesamtkapazität   = Plätze je Ebene × Regalebenen
 */
export function computeRackLayout(warehouse) {
    const length = Number(warehouse.length) || 0;
    const width = Number(warehouse.width) || 0;
    const palletWidth = Number(warehouse.palletWidth) || 0.8;
    const rackDepth = Number(warehouse.rackDepth) || 1.2;
    const aisleWidth = Number(warehouse.aisleWidth) || 3.2;
    const endAisle = Number(warehouse.endAisle) || 4.0;
    const levels = Math.max(1, Math.round(Number(warehouse.levels) || 1));

    const moduleWidth = 2 * rackDepth + aisleWidth;
    const usableWidth = Math.max(0, width - 2 * EDGE_MARGIN_M);
    const modules = moduleWidth > 0 ? Math.max(0, Math.floor(usableWidth / moduleWidth + 1e-9)) : 0;

    const rackRunLength = Math.max(0, length - 2 * endAisle);
    const slotsPerRow = palletWidth > 0 ? Math.max(0, Math.floor(rackRunLength / palletWidth + 1e-9)) : 0;

    const slotsPerLevel = modules * 2 * slotsPerRow;
    const totalCapacity = slotsPerLevel * levels;

    const totalArea = length * width;
    const rackArea = modules * 2 * rackDepth * rackRunLength;
    const trafficArea = Math.max(0, totalArea - rackArea);
    const trafficPercent = totalArea > 0 ? (trafficArea / totalArea) * 100 : 0;

    return {
        moduleWidth,
        modules,
        rackRunLength,
        slotsPerRow,
        slotsPerLevel,
        levels,
        totalCapacity,
        totalArea,
        rackArea,
        trafficArea,
        trafficPercent,
        params: {length, width, palletWidth, rackDepth, aisleWidth, endAisle, levels},
    };
}

/**
 * Sucht die kleinste quadratische Hallenseite, mit der die Zielkapazität
 * (z. B. die vom Auftraggeber bestätigten 500 Plätze in Weilheim) gerade
 * erreicht wird - Punkt "leite die Hallenmaße hier aus der Platzzahl ab,
 * statt sie vorzugeben" im Auftrag. Reine Iteration statt geschlossener
 * Formel, weil beide Kapazitätsanteile (Module, Stellplätze/Reihe) durch die
 * abgerundeten floor()-Schritte keine stetige Funktion der Seitenlänge sind.
 */
export function deriveSquareSideForCapacity(targetCapacity, rackParams, {maxSide = 150, step = 0.2} = {}) {
    for (let side = step; side <= maxSide; side += step) {
        const layout = computeRackLayout({...rackParams, length: side, width: side});
        if (layout.totalCapacity >= targetCapacity) {
            return Math.round(side * 10) / 10;
        }
    }
    return maxSide;
}

/**
 * Verteilt die Sortimente (bereits absteigend nach empfohlenem
 * Palettenbedarf sortiert) kaskadierend auf die Hallen. Jedes Element von
 * `warehouses` braucht ein Feld `capacity` (die errechnete Gesamtkapazität
 * aus computeRackLayout(), nicht mehr ein frei geschätzter Plätzewert) -
 * siehe Hinweis auf der Seite: eine Annahme dieser Oberfläche, keine
 * Vorgabe des Auftraggebers. Die größten Sortimente füllen zuerst Weilheim,
 * sobald dessen Kapazität überschritten würde, wandert der Rest
 * unwiderruflich in die nächste Halle - kein optimales Bin-Packing, aber in
 * der Oberfläche erklärbar.
 */
export function distributeRowsToWarehouses(rows, warehouses) {
    const assignments = warehouses.map((warehouse) => ({warehouse, rows: [], assignedPallets: 0}));
    const overflow = [];
    let hallIndex = 0;

    (rows || []).forEach((row) => {
        const need = palletsNeed(row);
        while (
            hallIndex < assignments.length &&
            assignments[hallIndex].assignedPallets + need > assignments[hallIndex].warehouse.capacity + 1e-6
        ) {
            hallIndex += 1;
        }
        if (hallIndex >= assignments.length) {
            overflow.push(row);
            return;
        }
        assignments[hallIndex].rows.push(row);
        assignments[hallIndex].assignedPallets += need;
    });

    return {assignments, overflow};
}

/**
 * Baut die Zeichenreihenfolge aller Regalreihen einer Halle (Modul für
 * Modul, je Modul vordere und hintere Reihe) und legt die zugeteilten
 * Sortimente linienweise entlang dieser Reihen ab - Punkt 4 im Auftrag: ein
 * Sortiment belegt so viele laufende Meter Regalreihe, wie sein empfohlener
 * Palettenbedarf über alle Ebenen hinweg braucht, notfalls über mehrere
 * Reihen hinweg. Reicht die Kapazität der Halle nicht für alle zugeteilten
 * Paletten (Rundungsdifferenzen der floor()-Schritte), bleibt der Rest
 * unsichtbar, statt eine Reihe zu überfüllen.
 */
export function layoutRackRows(assignedRows, warehouse) {
    const layout = computeRackLayout(warehouse);
    const {rackRunLength} = layout;
    const {rackDepth, aisleWidth, endAisle, palletWidth, levels} = layout.params;
    // Kommt die Kapazität aus der Kapazitätsdatei, ist die Zeichnung nur
    // noch schematisch: Die Regalreihen des Modells bleiben, tragen aber
    // gleichmäßig verteilt genau die gezählten Plätze. Ohne Plätze laut
    // Datei (K31) entfallen die Regalreihen ganz.
    const fileCapacity = warehouse.fileCapacity;
    const fromFile = fileCapacity !== null && fileCapacity !== undefined && Number.isFinite(Number(fileCapacity));
    const modules = fromFile && Number(fileCapacity) <= 0 ? 0 : layout.modules;
    const totalRowLength = modules * 2 * rackRunLength;
    const capacityPerMeter = fromFile
        ? (totalRowLength > 0 ? Number(fileCapacity) / totalRowLength : 0)
        : (palletWidth > 0 ? levels / palletWidth : 0);

    const rackRows = [];
    for (let m = 0; m < modules; m += 1) {
        const rowStartY = EDGE_MARGIN_M + m * (2 * rackDepth + aisleWidth);
        rackRows.push({
            rowNumber: rackRows.length + 1,
            moduleIndex: m,
            side: "vorne",
            x: endAisle,
            y: rowStartY,
            w: rackRunLength,
            h: rackDepth,
        });
        rackRows.push({
            rowNumber: rackRows.length + 1,
            moduleIndex: m,
            side: "hinten",
            x: endAisle,
            y: rowStartY + rackDepth,
            w: rackRunLength,
            h: rackDepth,
        });
    }

    const blocks = [];
    let rowIdx = 0;
    let usedInRow = 0;

    (assignedRows || []).forEach((row) => {
        let remainingPallets = palletsNeed(row);
        let segment = 0;
        while (remainingPallets > 1e-6 && rowIdx < rackRows.length && capacityPerMeter > 0) {
            const current = rackRows[rowIdx];
            const freeLength = current.w - usedInRow;
            if (freeLength <= 1e-6) {
                rowIdx += 1;
                usedInRow = 0;
                continue;
            }
            const metersNeeded = remainingPallets / capacityPerMeter;
            const metersUsed = Math.min(metersNeeded, freeLength);
            segment += 1;
            blocks.push({
                ...row,
                id: `${row.parent_article_number || row.article_group || "sortiment"}-${rowIdx}-${segment}`,
                rowNumber: current.rowNumber,
                moduleIndex: current.moduleIndex,
                side: current.side,
                levels,
                x: current.x + usedInRow,
                y: current.y,
                w: metersUsed,
                h: current.h,
                palletsHere: metersUsed * capacityPerMeter,
            });
            usedInRow += metersUsed;
            remainingPallets -= metersUsed * capacityPerMeter;
            if (usedInRow >= current.w - 1e-6) {
                rowIdx += 1;
                usedInRow = 0;
            }
        }
    });

    // Verbleibende freie Regalreihenmeter sichtbar machen (Rest der aktuellen
    // Reihe plus alle noch unbenutzten Reihen).
    if (rowIdx < rackRows.length) {
        const current = rackRows[rowIdx];
        if (usedInRow < current.w - 1e-6) {
            blocks.push({
                isFree: true,
                id: `free-${rowIdx}`,
                x: current.x + usedInRow,
                y: current.y,
                w: current.w - usedInRow,
                h: current.h,
            });
        }
        for (let i = rowIdx + 1; i < rackRows.length; i += 1) {
            const r = rackRows[i];
            blocks.push({isFree: true, id: `free-${i}`, x: r.x, y: r.y, w: r.w, h: r.h});
        }
    }

    // Quergänge an beiden Hallenenden (Fahrtrichtung entlang der Breite) und
    // Arbeitsgänge zwischen den Regalmodulen (Fahrtrichtung entlang der
    // Länge) - für die Zeichnung, siehe Auftrag Punkt 3. Der erste Quergang
    // dient zugleich als Zone für Wareneingang/-ausgang.
    const aisles = [];
    if (endAisle > 0) {
        aisles.push({role: "wareneingang", x: 0, y: 0, w: endAisle, h: layout.params.width, orientation: "vertical"});
        aisles.push({
            role: "quergang",
            x: Math.max(0, layout.params.length - endAisle),
            y: 0,
            w: endAisle,
            h: layout.params.width,
            orientation: "vertical",
        });
    }
    for (let m = 0; m < modules; m += 1) {
        const rowStartY = EDGE_MARGIN_M + m * (2 * rackDepth + aisleWidth);
        aisles.push({
            role: "arbeitsgang",
            x: 0,
            y: rowStartY + 2 * rackDepth,
            w: layout.params.length,
            h: aisleWidth,
            orientation: "horizontal",
        });
    }

    return {layout, rackRows, blocks, aisles};
}
